import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DialogService, SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { IImage } from 'lib/ui/ui-models.js';
import { FormFieldComponent } from '../form-field.component.js';

export type SelectItemField<TData = any> = {
	readonly title: string;
	readonly value: string | undefined;
	readonly data: TData;

	readonly avatar?: IImage;
	readonly avatarLocation?: SelectItemAvatarLocation;
	readonly subtitle?: string;
};

export type SelectItemAvatarLocation = 'start' | 'end';
export type SelectItemMode = 'chips' | 'tooltipMenu' | 'dialog';
export type SelectItemDesign = 'default' | 'card';

@Component({
	selector: 'ui-select-item-field',
	templateUrl: './select-item-field.component.html',
	styleUrls: ['../form-fields.shared.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SelectItemFieldComponent extends FormFieldComponent<string | undefined> {
	private readonly dialogModeDisplayThreshold: number = 10;

	public readonly addClearButton = input<boolean>(false);
	public readonly items = input.required<readonly SelectItemField[]>();
	public readonly mode = input<SelectItemMode>('tooltipMenu');
	public readonly design = input<SelectItemDesign>('default');

	public readonly selectedItemChange = output<SelectItemField<any>>();
	public readonly clearButtonClick = output<void>();

	public readonly displayValue = computed<string>(() => {
		const currentValue = this.currentValue();
		if (!currentValue) {
			return '';
		}
		return (
			this.items().find((m) => m?.value?.toLowerCase() === currentValue?.toString()?.toLowerCase())?.title ?? ''
		);
	});

	public readonly subtitleValue = computed<string | undefined>(() => {
		const currentValue = this.currentValue();

		if (!currentValue) {
			return undefined;
		}
		return (
			this.items().find((m) => m?.value?.toLowerCase() === currentValue?.toString()?.toLowerCase())?.subtitle ??
			''
		);
	});

	public readonly calculatedMode = computed<SelectItemMode>(() => {
		if (this.items().length >= this.dialogModeDisplayThreshold) {
			return 'dialog';
		}

		return this.mode();
	});

	public readonly showClearButton = computed<boolean>(() => {
		return this.addClearButton() && this.currentValue() !== undefined;
	});

	constructor(private readonly dialogService: DialogService) {
		super();
	}

	handleSelectItem(item: SelectItemField<string>): void {
		super.updateValue(item.value);
		this.selectedItemChange.emit(item);
	}

	isItemActive(item: SelectItemField<string>): boolean {
		return item.value === this.currentValue?.();
	}

	openSelectionDialog(): void {
		this.dialogService.openSelectionDialog<SelectItemField>({
			title: this.title(),
			subtitle: this.subtitle(),
			select: (item) => {
				this.handleSelectItem(item);
			},
			items: this.items().map((item) => {
				const selectionItem: SelectionDialogItem<SelectItemField> = {
					data: item,
					id: item.value ?? '__',
					title: item.title,
					subtitle: item.subtitle
				};

				return selectionItem;
			})
		});
	}

	handleClear(): void {
		this.updateValue(undefined);
		this.clearButtonClick.emit();
	}

	protected getHint(value?: string): string | undefined {
		return undefined;
	}

	protected isValueValid(value?: string): boolean {
		return true;
	}
}
